import React from "react";
import { toast } from "react-toastify";
import { useDeclineProRequest, useSaveBrokerageSeats } from "@/hooks/useBrokerageManage";
import "./BrokerageProRequests.scss";

const nameOf = (agent) => [agent?.firstName, agent?.lastName].filter(Boolean).join(" ") || "An agent";

export const askedOn = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

export const requestsLineOf = (requests, failed = false) => {
  if (failed) return "Requests did not load";
  const count = (requests || []).length;
  if (!count) return "No agents have asked you to cover Gitcha Pro";
  if (count === 1) return `${nameOf(requests[0].agent)} asked you to cover Gitcha Pro`;
  return `${count} agents asked you to cover Gitcha Pro`;
};

const initialsOf = (agent) =>
  [agent?.firstName, agent?.lastName]
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

export const seatsWith = (current, added) =>
  [...new Set([...(current || []), ...(added || [])].map(Number).filter((id) => Number.isInteger(id) && id > 0))];

const BrokerageProRequests = ({ brokerageId, requests = [], onCover, planActive = false, seatUserIds = [], failed = false }) => {
  const decline = useDeclineProRequest(brokerageId);
  const saveSeats = useSaveBrokerageSeats(brokerageId);
  const cover = (agents) => {
    const ids = agents.map((agent) => agent?.userId).filter(Boolean);
    if (!planActive) return onCover(ids);
    const names = agents.length === 1 ? nameOf(agents[0]) : `${agents.length} agents`;
    return saveSeats.mutate(seatsWith(seatUserIds, ids), {
      onSuccess: () => toast.success(`${names} now ${agents.length === 1 ? "has" : "have"} Gitcha Pro from your office.`),
      onError: (error) => toast.error(error.message),
    });
  };
  if (!requests.length && failed) {
    return <p className="pro-requests__empty">Your agents' requests did not load. Close this and open it again to try again.</p>;
  }
  if (!requests.length) {
    return (
      <p className="pro-requests__empty">
        When an agent taps Ask your broker on a Gitcha Pro feature, their request shows here. Covering an agent who asked
        cancels their own Pro and refunds the days&nbsp;left.
      </p>
    );
  }
  return (
    <div className="pro-requests">
      <ul className="pro-requests__list">
        {requests.map((request) => (
          <li key={request.id} className="pro-requests__row">
            <span className="pro-requests__avatar" aria-hidden="true">
              {request.agent?.photo ? <img src={request.agent.photo} alt="" loading="lazy" /> : initialsOf(request.agent)}
            </span>
            <span className="pro-requests__who">
              <strong>{nameOf(request.agent)}</strong>
              <span>
                {[`Asked ${askedOn(request.createdAt)}`, request.agent?.planName ? `on ${request.agent.planName}` : null]
                  .filter(Boolean)
                  .join(", ")}
              </span>
              {request.note ? <span className="pro-requests__note">{request.note}</span> : null}
            </span>
            <span className="pro-requests__actions">
              <button
                type="button"
                className="pro-requests__link"
                disabled={decline.isPending}
                onClick={() =>
                  decline.mutate(request.id, {
                    onSuccess: () => toast.success(`${nameOf(request.agent)} was told you are not covering Pro now.`),
                    onError: (error) => toast.error(error.message),
                  })
                }
              >
                Decline
              </button>
              <button
                type="button"
                className="btn btn-primary pro-requests__cover"
                disabled={saveSeats.isPending}
                onClick={() => cover([request.agent])}
              >
                Cover Pro
              </button>
            </span>
          </li>
        ))}
      </ul>
      {requests.length > 1 ? (
        <button
          type="button"
          className="pro-requests__link pro-requests__all"
          disabled={saveSeats.isPending}
          onClick={() => cover(requests.map((request) => request.agent))}
        >
          Cover all {requests.length}
        </button>
      ) : null}
    </div>
  );
};

export default BrokerageProRequests;
